import { useCallback, useEffect, useRef } from 'react';

// Consecutive frames the target offset must hold still before layout is considered settled.
const STABLE_FRAMES = 3;
const DRIFT_TOLERANCE_PX = 2;
const MAX_SETTLE_MS = 10000;
const ABORT_EVENTS = ['wheel', 'touchstart', 'keydown'];
const NO_OFFSET = () => 0;

/**
 * Scrolls to an element and keeps it pinned while lazily hydrated sections above it reflow.
 *
 * Deep links land on a page where most sections are still placeholders of estimated height,
 * so a one-shot scroll aims at an offset that is invalidated moments later. Rather than
 * guessing a delay, this re-resolves the target and its offset every animation frame and
 * re-corrects on drift, stopping once the offset is stable, the user scrolls, or the cap hits.
 *
 * `getTarget` is re-invoked each frame, so it may return null until the element exists.
 *
 * @param {Function} [getOffset] - pixels to leave above the element, read every frame so
 * a sticky header that resizes mid-scroll stays accounted for.
 */
export const useAnchorScroll = (getOffset = NO_OFFSET) => {
  const frameRef = useRef(null);
  const detachRef = useRef(null);
  const disarmedRef = useRef(false);
  const getOffsetRef = useRef(getOffset);

  getOffsetRef.current = getOffset;

  const stop = useCallback(() => {
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    if (detachRef.current) {
      detachRef.current();
      detachRef.current = null;
    }
  }, []);

  const anchor = useCallback((getTarget) => {
    if (typeof window === 'undefined' || disarmedRef.current) return;

    stop();

    // Once the user takes control of the viewport, never auto-scroll again.
    const abort = () => {
      disarmedRef.current = true;
      stop();
    };
    ABORT_EVENTS.forEach((event) => window.addEventListener(event, abort, { passive: true }));
    detachRef.current = () => {
      ABORT_EVENTS.forEach((event) => window.removeEventListener(event, abort));
    };

    // Same clock requestAnimationFrame stamps each frame with, so the cap is immune to
    // the system clock being adjusted mid-scroll.
    const deadline = window.performance.now() + MAX_SETTLE_MS;
    let lastTop = null;
    let stableFrames = 0;

    const step = (now) => {
      frameRef.current = null;

      if (now > deadline) {
        stop();
        return;
      }

      const target = getTarget();
      if (target) {
        const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - getOffsetRef.current());

        if (lastTop !== null && Math.abs(top - lastTop) <= DRIFT_TOLERANCE_PX) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          // Reflow above the target often moves it and the viewport together, leaving us
          // already where we want to be; scrolling anyway would dispatch `scroll` to the
          // whole page and hydrate more content, moving the target again.
          if (Math.abs(window.scrollY - top) > DRIFT_TOLERANCE_PX) {
            window.scrollTo({ top, behavior: 'auto' });
          }
        }
        lastTop = top;

        if (stableFrames >= STABLE_FRAMES) {
          stop();
          return;
        }
      }

      frameRef.current = window.requestAnimationFrame(step);
    };

    frameRef.current = window.requestAnimationFrame(step);
  }, [stop]);

  useEffect(() => stop, [stop]);

  return anchor;
};
